/**
 * 当前方言 —— 前端侧的状态
 * ============================================================
 *
 * 为什么需要这个：
 *   方言发音由**音色**决定，而音色是后端按方言路由的。
 *   如果不把方言传给后端，后端就只能用普通话音色，
 *   于是方言文本会被用**普通话发音**读出来 —— 等于教错。
 *
 *   （早期这里写的是"方言效果完全由 instruction 决定"，那是错的：
 *     instruction 只给普通话镀层薄口音，用户验收时指出"张冠李戴"。
 *     正确做法见 config/dialects.js 的 tts.voice 与 services/tts-qwen3.js。）
 *
 *   所以「当前方言」不是一个 UI 装饰，而是音频正确性的必要条件。
 *
 * 存储：localStorage（与课程进度一致，纯本机、不涉及账号）
 */

import api from './api';

const KEY = 'dialectgo_current_dialect';

/** 默认方言。粤语是当前唯一有完整课程内容的方言。 */
export const DEFAULT_DIALECT_ID = 'yue-HK';

/**
 * 读取当前方言 id
 * @returns {string}
 */
export function getSelectedDialectId() {
  try {
    return localStorage.getItem(KEY) || DEFAULT_DIALECT_ID;
  } catch {
    return DEFAULT_DIALECT_ID; // localStorage 不可用时也不崩
  }
}

/**
 * 设置当前方言
 * @param {string} id
 */
export function setSelectedDialectId(id) {
  try {
    localStorage.setItem(KEY, String(id));
  } catch {
    /* 忽略：设置不上不影响本次会话 */
  }
}

/**
 * 拉取方言目录（含能力状态与质量数据）
 * @param {Object} [params] - { system, ready }
 * @returns {Promise<Object>}
 */
export async function fetchDialects(params = {}) {
  const res = await api.get('/api/dialects', { params });
  return res.data?.data || { dialects: [], summary: null, systems: [] };
}

/**
 * 拉取单个方言详情
 * @param {string} id
 */
export async function fetchDialect(id) {
  const res = await api.get(`/api/dialects/${id}`);
  return res.data?.data || null;
}

/**
 * 把能力状态映射成 UI 配色
 * 与后端 config/dialects.js 的 STATUS 一一对应。
 */
export const STATUS_STYLE = {
  released: { label: '已支持', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  tested: { label: '测试可用', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  experimental: { label: '实验中', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  partial: { label: '部分支持', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  unavailable: { label: '暂不支持', cls: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400' },
};
